import { create } from "zustand";

/* ── Types ─────────────────────────────────────────────── */

export type Module =
  | "SSP"
  | "POAM"
  | "ASSESSMENT_PLAN"
  | "ASSESSMENT_RESULTS"
  | "CATALOG"
  | "PROFILE"
  | "COMPONENT_DEFINITION"
  | "GOVERNANCE_DOCUMENT"
  | "EVIDENCE"
  | "CONTENT_REGISTRY";

export type Frequency = "DAILY" | "WEEKLY" | "MONTHLY";

export type Weekday = "SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT";

/** One configured criteria entry, shown as a row in a scheduler's Configuration page. */
export interface CriteriaConfig {
  id: string;
  organization: string;
  documentCategory: string;
  documentType: string;
  poamLeadValue: string;
  poamLeadUnit: string;
  updatedAt: string;
}

export interface AlertConfig {
  id: string;
  module: Module;
  frequency: Frequency;
  /**
   * Shape depends on `frequency`: the literal "DAILY", a comma-joined list of
   * weekday codes for WEEKLY (e.g. "MON,WED,FRI"), or a comma-joined list of
   * ISO dates for MONTHLY (e.g. "2026-08-01,2026-08-15").
   */
  importOn: string;
  importAt: string;
  criteriaList: CriteriaConfig[];
  active: boolean;
  createdByName: string;
  updatedByName: string;
  createdAt: string;
  updatedAt: string;
}

/** The signed-in user is not wired into this UI-only mock yet. */
export const CURRENT_USER_NAME = "sysadmin";

/** Backend doesn't process this field on writes yet, but the scheduler content-review
 *  APIs expect/return it - kept here so every caller sends the same value. */
export const SCHEDULER_MODULE_NAME = "CONTENT_REGISTRY_REVIEW";

/** Turn a raw SNAKE_CASE enum value (e.g. "CONTENT_REGISTRY_REVIEW") into a
 *  readable label ("Content Registry Review"). */
export function formatEnumLabel(value: string): string {
  if (!value) return "-";
  return value
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/* ── Static options ────────────────────────────────────── */

export const MODULE_OPTIONS: { value: Module; label: string }[] = [
  { value: "CONTENT_REGISTRY", label: "Content Registry" },
];

export const MODULE_LABEL: Record<Module, string> = Object.fromEntries(
  MODULE_OPTIONS.map((o) => [o.value, o.label]),
) as Record<Module, string>;

export const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

export const FREQUENCY_LABEL: Record<Frequency, string> = Object.fromEntries(
  FREQUENCY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<Frequency, string>;

export const WEEKDAY_OPTIONS: { value: Weekday; label: string }[] = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WED", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
];

export const WEEKDAY_LABEL: Record<Weekday, string> = Object.fromEntries(
  WEEKDAY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<Weekday, string>;

export const WEEKDAY_SHORT_LABEL: Record<Weekday, string> = Object.fromEntries(
  WEEKDAY_OPTIONS.map((o) => [o.value, o.label.slice(0, 3)]),
) as Record<Weekday, string>;

/** `importOn` for WEEKLY stores selected weekdays as a comma-joined code list (e.g. "MON,WED,FRI"). */
export function isWeekdayListValue(value: string): boolean {
  if (!value) return false;
  const codes = value.split(",").filter(Boolean);
  return codes.length > 0 && codes.every((c) => c in WEEKDAY_LABEL);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `importOn` for MONTHLY stores selected calendar dates as a comma-joined ISO date list. */
export function isDateListValue(value: string): boolean {
  if (!value) return false;
  const dates = value.split(",").filter(Boolean);
  return dates.length > 0 && dates.every((d) => ISO_DATE_RE.test(d));
}

export const ORGANIZATION_OPTIONS = [
  { value: "ACME_FINANCE", label: "Acme Corp - Finance" },
  { value: "ACME_SECURITY", label: "Acme Corp - Security" },
  { value: "ACME_OPERATIONS", label: "Acme Corp - Operations" },
];

export const ORGANIZATION_LABEL: Record<string, string> = Object.fromEntries(
  ORGANIZATION_OPTIONS.map((o) => [o.value, o.label]),
);

export const DOCUMENT_CATEGORY_OPTIONS = [
  { value: "GOVERNANCE", label: "Governance" },
  { value: "EVIDENCE", label: "Evidence" },
];

export const DOCUMENT_CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  DOCUMENT_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

export const DOCUMENT_TYPE_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "STANDARD", label: "Standard" },
  { value: "POLICY", label: "Policy" },
  { value: "GUIDELINE", label: "Guideline" },
  { value: "PROCEDURES", label: "Procedures" },

];

export const DOCUMENT_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  DOCUMENT_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

export const POAM_LEAD_UNIT_OPTIONS = [
  { value: "DAYS_BEFORE", label: "days before" },
  { value: "WEEKS_BEFORE", label: "weeks before" },
  { value: "MONTHS_BEFORE", label: "months before" },
];

export const POAM_LEAD_UNIT_LABEL: Record<string, string> = Object.fromEntries(
  POAM_LEAD_UNIT_OPTIONS.map((o) => [o.value, o.label]),
);

export function defaultCriteriaDraft() {
  return {
    organization: "ACME_FINANCE",
    documentCategory: "GOVERNANCE",
    documentType: "STANDARD",
    poamLeadValue: "15",
    poamLeadUnit: "DAYS_BEFORE",
  };
}

function makeCriteria(overrides: Partial<CriteriaConfig>, updatedAt: string): CriteriaConfig {
  return {
    id: overrides.id ?? `criteria-${updatedAt}-${Math.round(Math.random() * 1e6)}`,
    ...defaultCriteriaDraft(),
    ...overrides,
    updatedAt,
  };
}

/* ── Seed data (UI-only until an Alert Config API is wired up) ── */

const SEED_ALERTS: AlertConfig[] = [
  {
    id: "alert-1",
    module: "CONTENT_REGISTRY",
    frequency: "DAILY",
    importOn: "DAILY",
    importAt: "10:00",
    criteriaList: [
      makeCriteria({ id: "criteria-1-1" }, "2026-07-26T15:41:19.902Z"),
      makeCriteria(
        { id: "criteria-1-2", organization: "ACME_SECURITY", documentCategory: "EVIDENCE" },
        "2026-07-20T09:12:00.000Z",
      ),
    ],
    active: true,
    createdByName: "sysadmin",
    updatedByName: "sysadmin",
    createdAt: "2026-07-01T10:22:03.118Z",
    updatedAt: "2026-07-26T15:41:19.902Z",
  },
];

/* ── Store ─────────────────────────────────────────────── */

interface SchedulersStore {
  alerts: AlertConfig[];
  setAlerts: (updater: (prev: AlertConfig[]) => AlertConfig[]) => void;
  addCriteria: (alertId: string, data: Omit<CriteriaConfig, "id" | "updatedAt">) => void;
  updateCriteria: (
    alertId: string,
    criteriaId: string,
    data: Omit<CriteriaConfig, "id" | "updatedAt">,
  ) => void;
  deleteCriteria: (alertId: string, criteriaId: string) => void;
}

export const useSchedulersStore = create<SchedulersStore>((set) => ({
  alerts: SEED_ALERTS,

  setAlerts: (updater) => set((s) => ({ alerts: updater(s.alerts) })),

  addCriteria: (alertId, data) =>
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.id === alertId
          ? {
              ...a,
              criteriaList: [
                makeCriteria({ ...data }, new Date().toISOString()),
                ...a.criteriaList,
              ],
            }
          : a,
      ),
    })),

  updateCriteria: (alertId, criteriaId, data) =>
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.id === alertId
          ? {
              ...a,
              criteriaList: a.criteriaList.map((c) =>
                c.id === criteriaId
                  ? { ...c, ...data, updatedAt: new Date().toISOString() }
                  : c,
              ),
            }
          : a,
      ),
    })),

  deleteCriteria: (alertId, criteriaId) =>
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.id === alertId
          ? { ...a, criteriaList: a.criteriaList.filter((c) => c.id !== criteriaId) }
          : a,
      ),
    })),
}));

export function formatScheduleTime(time: string) {
  if (!time) return "—";
  const [hStr, mStr] = time.split(":");
  return `${hStr.padStart(2, "0")}:${mStr}`;
}

export function formatScheduleTimeDisplay(time: string) {
  if (!time) return "—";
  const [hStr, mStr] = time.split(":");
  const hour = parseInt(hStr, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${mStr} ${period}`;
}

export function formatDateTime(iso: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
